'use client';

import { useContext, useMemo } from 'react';

import { FormFieldContext } from '../form-field';
import { ComboboxFloatingContext } from './context/combobox-floating.context';
import { ComboboxRootContext } from './context/combobox-root.context';
import { useCombobox } from './hooks/use-combobox';
import type { ComboboxRootPropsInterface, ComboboxValueType } from './types/combobox-root.types';

/**
 * @since 8.28.0
 */
export const ComboboxRoot = <ValueType extends ComboboxValueType>(
  props: ComboboxRootPropsInterface<ValueType>,
) => {
  const formFieldContext = useContext(FormFieldContext);

  const {
    autoCompleteBehavior = 'automatic',
    appearance = 'select',
    children,
    disabled = formFieldContext.disabled || false,
    dividers = false,
    editable = true,
    error = formFieldContext.error || false,
    limitChipsShown,
    freeForm = false,
    loading = false,
    placeholder,
    readOnly = formFieldContext.readOnly || false,
    required = formFieldContext.required || false,
    showOptionControl = false,
    size = 'md',
    showLabelForValue = false,
  } = props;

  const { combobox, floating } = useCombobox({ ...props, readOnly });

  const contextValue = useMemo(
    () => ({
      autoCompleteBehavior,
      appearance,
      combobox,
      controlId: formFieldContext.controlId,
      descriptionId: formFieldContext.descriptionId,
      disabled,
      dividers,
      editable,
      error,
      freeForm,
      limitChipsShown,
      loading,
      placeholder,
      readOnly,
      required,
      showOptionControl,
      size,
      showLabelForValue,
    }),
    [
      autoCompleteBehavior,
      appearance,
      combobox,
      formFieldContext.controlId,
      formFieldContext.descriptionId,
      disabled,
      dividers,
      editable,
      error,
      freeForm,
      limitChipsShown,
      loading,
      placeholder,
      readOnly,
      required,
      showOptionControl,
      size,
      showLabelForValue,
    ],
  );

  return (
    <ComboboxRootContext.Provider value={contextValue}>
      <ComboboxFloatingContext.Provider value={floating}>
        {children}
      </ComboboxFloatingContext.Provider>
    </ComboboxRootContext.Provider>
  );
};

ComboboxRoot.displayName = 'Combobox.Root';
